import { DataGrid, GridColDef } from "@mui/x-data-grid";
import ErrorIcon from '@mui/icons-material/Error';
import Paper from "@mui/material/Paper";
import {
  Box,
  CircularProgress,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  getColorByValue,
  roundNumberToStr,
} from "../../../helpers/utils";
import { ArrowDropUp, Palette } from "@mui/icons-material";
import { ArrowDropDown } from "@mui/icons-material";
import { StringToNumberDictionary } from "../../../types/stringToNumberDictionary";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  getOverallActivitiesResults,
} from "../../../api/getOverviewData";
import { useTranslation } from "react-i18next";
import { groupedActivityTags } from "../../../helpers/selectOptions";
import { OverallActivityResult } from "../../../types/overallActivityResult";

const greenDecrease = "rgb(21, 187, 118)";
const redIncrease = "rgb(245, 35, 80)";
const paginationModel = { page: 0, pageSize: 8 };


const deviceDictionary: { [key: string]: string } = {
  Back: "MLI® Back",
  Elbow: "MLI® Elbow"
};

type OverallActivitiesResultsTableProps = {
  height?: number | string;
  projectCodes: string[];
};

export type OverallActivitiesResultsTableRef ={
  getRows : () => OverallActivityResult[];
  isReady : () => boolean;
}

const OverallActivitiesResultsTable = forwardRef<
  OverallActivitiesResultsTableRef,
  OverallActivitiesResultsTableProps
>(function OverallActivitiesResultsTable( { height, projectCodes} , ref) {

  if (height === undefined) {
    height = "100%";
  }

  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<OverallActivityResult[]>([]);
  const [mliEvol, setMliEvol] = useState<StringToNumberDictionary>({});
  const [painEvol, setPainEvol] = useState<StringToNumberDictionary>({});
  const { t } = useTranslation();
  const theme = useTheme();


  useImperativeHandle(ref, () => ({
    getRows: () => rows,
    isReady: () => isLoading
  }));

  useEffect(() => {
    let controller = new AbortController();
    let isMounted = true;
    const fetchOverallActivityResults = async () => {
      try {
        setIsLoading(true);

        // Remove data from unselected project codes
        setRows([]);
        setMliEvol({});
        setPainEvol({});

        // map uses a unique id to prevent duplicates
        const rowMap = new Map<number, OverallActivityResult>();
        const mliEvolObj: StringToNumberDictionary = {};
        const painEvolObj: StringToNumberDictionary = {};
        for (const pc of projectCodes) {
          const data = await getOverallActivitiesResults(pc, controller);
          for (const overallResult of data) {
            if (overallResult.activityData.activityName === null || overallResult.activityData.activityName === undefined) continue;

            const id =
              overallResult.activityData.activityName
                .split("")
                .reduce((acc, char) => acc + char.charCodeAt(0), 0) +
              overallResult.projectCode
                .split("")
                .reduce((acc, char) => acc + char.charCodeAt(0), 0);
      
       
           
         
            overallResult.id = id;
            rowMap.set(id, overallResult);
            mliEvolObj[overallResult.activityData.activityName] = overallResult.mliProgress;
            painEvolObj[overallResult.activityData.activityName] = overallResult.painProgress;
      
          }
        }
     
        setRows(Array.from(rowMap.values()));
        setMliEvol(mliEvolObj);
        setPainEvol(painEvolObj);
      } catch (error: any) {
        if (error.name === "AbortError") {
          console.log("Fetch aborted");
        } else {
          console.error("Fetch error:", error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchOverallActivityResults();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [projectCodes]);

  const columns: GridColDef[] = [
    { field: "activityName", headerName: "ACTIVITY NAME", width: 300, valueGetter : (_ , row) => row.activityData.activityName },
     {field: "deviceType", headerName: "DEVICE", width: 150, valueFormatter : (value) =>  deviceDictionary[value] },
    { field: "projectCode", headerName: "PROJECT CODE", width: 150 },
    {
      field: "mli",
      headerName: "AVERAGE MLI",
      width: 180,
      valueGetter : (_, row) => {
        let value = { mli: row.activityData.average.mli, activityName: row.activityData.activityName, cutoffDate: row.cutoffDate}
        return value
      },
       renderCell: (params) => 
        (
        
        <Box display="flex" alignItems="center">
          <Box
            component="span"
            sx={{
              height: 10,
              width: 10,
              borderRadius: "50%",
              backgroundColor: getColorByValue(
                params.value.mli as number,
                "MLI",
                true
              ),
              marginRight: 1,
            }}
          />
          {params.value.mli.toFixed(1)}
          {/* {!mliEvol[params.row.activity] && (
            <Skeleton sx={{ marginLeft: 1 }} variant="text" width={50} />
          )} */}
          {!params.value.cutoffDate && 
            <Tooltip title={t('no-post-measurement-tooltip')}> 
              <ErrorIcon sx={{color: theme.palette.primaryChart, opacity: 0.6, marginLeft: 2}}></ErrorIcon>
            </Tooltip> 
          }
          {params.value.cutoffDate && mliEvol[params.value.activityName] &&
            Math.abs(mliEvol[params.value.activityName]) >= 0.1 &&
            isNaN(mliEvol[params.value.activityName]) === false && (
              <Box
                sx={{
                  marginLeft: 1,
                  color:
                    mliEvol[params.value.activityName] < 0
                      ? greenDecrease
                      : redIncrease,
                }}
                justifyContent={"center"}
                alignItems={"center"}
                display={"flex"}
              >
                {mliEvol[params.value.activityName] < 0 ? (
                  <ArrowDropDown sx={{ color: greenDecrease }} />
                ) : (
                  <ArrowDropUp sx={{ color: redIncrease }} />
                )}
                <Typography variant="caption">
                  {(mliEvol[params.value.activityName] > 0 ? "+" : "-") +
                    roundNumberToStr(mliEvol[params.value.activityName], true, 1)}
                </Typography>
              </Box>
            )}
        </Box>
      ),
    },
    {
      field: "pain",
      headerName: "AVERAGE PAIN",
      width: 180,
      valueGetter : (_, row) => {
        let value = { pain: row.activityData.average.pain, activityName: row.activityData.activityName, cutoffDate: row.cutoffDate}
        return value
      },
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Box
            component="span"
            sx={{
              height: 10,
              width: 10,
              borderRadius: "50%",
              backgroundColor: getColorByValue(
                params.value.pain as number,
                "pain",
                true
              ),
              marginRight: 1,
            }}
          />
          {params.value.pain.toFixed(1)}
          {/* {!mliEvol[params.row.activity] && (
            <Skeleton sx={{ marginLeft: 1 }} variant="text" width={50} />
          )} */}

          {!params.value.cutoffDate && 
            <Tooltip title={t('no-post-measurement-tooltip')}> 
              <ErrorIcon sx={{color: theme.palette.primaryChart, opacity: 0.6, marginLeft: 2}}></ErrorIcon>
            </Tooltip> 
          }
          {params.value.cutoffDate && painEvol[params.value.activityName] &&
            Math.abs(painEvol[params.value.activityName]) >= 0.1 &&
            isNaN(painEvol[params.value.activityName]) === false && (
              <Box
                sx={{
                  marginLeft: 1,
                  color:
                    painEvol[params.value.activityName] < 0
                      ? greenDecrease
                      : redIncrease,
                }}
                justifyContent={"center"}
                alignItems={"center"}
                display={"flex"}
              >
                {painEvol[params.value.activityName] < 0 ? (
                  <ArrowDropDown sx={{ color: greenDecrease }} />
                ) : (
                  <ArrowDropUp sx={{ color: redIncrease }} />
                )}
                <Typography variant="caption">
                  {(painEvol[params.value.activityName] > 0 ? "+" : "-") +
                    roundNumberToStr(painEvol[params.value.activityName], true, 1)}
                </Typography>
              </Box>
            )}
        </Box>
      ),
    },
    { field: "riskFactors", headerName: "MAIN RISK FACTORS", width: 300, renderCell : (params) =>  {
      const f = params.value.map((riskFactor : any) =>
                  t("tags." + riskFactor, groupedActivityTags)
                )
                .join(", ")
      return f;

    }  },
  ];

  return rows.length > 0 ? (
    <Paper
      sx={{
        borderRadius: "5px",
        height: height,
        width: "100%",
        position: "relative",
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        initialState={{ pagination: { paginationModel } }}
        sx={{
          border: 0,
          display: "grid",
          gridTemplateRows: "auto 1f auto",
          borderRadius: 10,

          "& .MuiDataGrid-columnHeaders": {
            color: theme.palette.text.secondary, // Header text color
          },
          "& .MuiDataGrid-cell": {
            color: theme.palette.text.primary,
          },
          "&, [class^=MuiDataGrid]": { border: "none" },
        }}
      />
      {isLoading && (
        <Grid
          container
          flexDirection={"row"}
          spacing={1}
          sx={{ position: "absolute", bottom: 12, width: "100%" }}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Grid item>
            <Typography>Loading more..</Typography>
          </Grid>
          <Grid item>
            <CircularProgress color="primary" size="20px" />
          </Grid>
        </Grid>
      )}
    </Paper>
  ) : (
    <Skeleton
      sx={{
        borderRadius: "5px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
      }}
      animation="pulse"
      variant="rectangular"
      width={"100%"}
      height={height}
    />
  );
})

export {OverallActivitiesResultsTable };